import { Bell, ChevronLeft, LogOut, ScanSearch, Send, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { BrandHeader } from '../components/BrandHeader';
import { InstallPrompt } from '../components/InstallPrompt';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { getPushDebug, sendTestPush } from '../services/api';
import { getDeviceId } from '../services/device';
import { getClientPushDiagnostics } from '../services/push';
import { APP_VERSION, type Username } from '../utils/constants';
import { toFriendlyError } from '../utils/errors';

type SettingsPageProps = {
  username: Username;
  token: string;
  onBack: () => void;
  onLogout: () => Promise<void>;
};

export function SettingsPage({ username, token, onBack, onLogout }: SettingsPageProps) {
  const push = usePushNotifications(token);
  const [testMessage, setTestMessage] = useState('');
  const [isTesting, setIsTesting] = useState(false);
  const [debugMessage, setDebugMessage] = useState('');
  const [isDebugging, setIsDebugging] = useState(false);

  async function handleTestPush() {
    setIsTesting(true);
    setTestMessage('');

    try {
      const response = await sendTestPush(token, getDeviceId());
      const attemptCopy = response.delivery.attempts === 1 ? '1 attempt' : `${response.delivery.attempts} attempts`;
      setTestMessage(`Test accepted by push service after ${attemptCopy}.`);
    } catch (error: unknown) {
      setTestMessage(toFriendlyError(error));
    } finally {
      setIsTesting(false);
    }
  }

  async function handleDebugPush() {
    setIsDebugging(true);
    setDebugMessage('');

    try {
      const [client, server] = await Promise.all([getClientPushDiagnostics(), getPushDebug(token, getDeviceId())]);
      const lines = [
        `Installed app: ${client.isStandalone ? 'yes' : 'no'}`,
        `Secure HTTPS: ${client.isSecureContext ? 'yes' : 'no'}`,
        `Push supported: ${client.hasPushManager ? 'yes' : 'no'}`,
        `Permission: ${client.notificationPermission}`,
        `Browser subscription: ${client.hasBrowserSubscription ? 'yes' : 'no'}`,
        `KV subscription: ${server.push.hasStoredSubscription ? 'yes' : 'no'}`,
        `Registered devices: ${server.push.deviceCount}`,
        `KV binding: ${server.cloudflare.hasKvBinding ? 'yes' : 'no'}`,
        `VAPID public/private/subject: ${server.cloudflare.hasVapidPublicKey && server.cloudflare.hasVapidPrivateKey && server.cloudflare.hasVapidSubject ? 'yes' : 'no'}`,
      ];

      if (client.endpointHost) {
        lines.push(`Push host: ${client.endpointHost}`);
      }

      setDebugMessage(lines.join('\n'));
    } catch (error: unknown) {
      setDebugMessage(toFriendlyError(error));
    } finally {
      setIsDebugging(false);
    }
  }

  return (
    <main className="app-shell auth-shell">
      <section className="hero-panel settings-panel" aria-labelledby="settings-title">
        <BrandHeader
          eyebrow="Settings"
          action={
            <div className="top-actions">
              <InstallPrompt />
              <button className="icon-action" type="button" onClick={onBack} aria-label="Back to home">
                <ChevronLeft size={18} aria-hidden="true" />
              </button>
            </div>
          }
        />

        <div className="settings-list">
          <div className="settings-item">
            <span className="permission-icon success">
              <ShieldCheck size={22} aria-hidden="true" />
            </span>
            <div>
              <p className="panel-kicker">Current user</p>
              <h2 id="settings-title">{username}</h2>
            </div>
          </div>

          <div className="settings-item">
            <span className="permission-icon">
              <Bell size={22} aria-hidden="true" />
            </span>
            <div>
              <p className="panel-kicker">Notifications</p>
              <h2>{push.permission}</h2>
              <p>{push.message || 'Re-register this phone if notifications stop arriving.'}</p>
            </div>
          </div>

          <button className="notify-button" type="button" onClick={push.register} disabled={push.isRegistering}>
            <Bell size={18} aria-hidden="true" />
            {push.isRegistering ? 'Registering...' : 'Re-register notifications'}
          </button>

          <button className="secondary-button full-width" type="button" onClick={handleTestPush} disabled={isTesting}>
            <Send size={18} aria-hidden="true" />
            {isTesting ? 'Sending test...' : 'Send test notification'}
          </button>

          {testMessage ? <p className="result-banner">{testMessage}</p> : null}

          <button className="secondary-button full-width" type="button" onClick={handleDebugPush} disabled={isDebugging}>
            <ScanSearch size={18} aria-hidden="true" />
            {isDebugging ? 'Checking...' : 'Run notification check'}
          </button>

          {debugMessage ? <pre className="debug-panel">{debugMessage}</pre> : null}

          <button className="secondary-button full-width" type="button" onClick={onLogout}>
            <LogOut size={18} aria-hidden="true" />
            Logout
          </button>
        </div>

        <aside className="safety-note">
          <ShieldCheck size={18} aria-hidden="true" />
          <p>This app stores only session and push notification information in Cloudflare KV. Version {APP_VERSION}.</p>
        </aside>
      </section>
    </main>
  );
}
